import { ref, onMounted, onUnmounted, watch, Ref } from 'vue';

interface IntersectionObserverOnceOptions extends IntersectionObserverInit {}

export function useIntersectionObserverOnce(
  target: Ref<HTMLElement | null>,
  options: IntersectionObserverOnceOptions = {},
  callback?: () => void,
) {
  const wasIntersected = ref(false);
  let observer: IntersectionObserver | null = null;

  const defaultOptions = {
    root: null,
    rootMargin: '0px',
    threshold: [0],
    ...options,
  };

  const observe = () => {
    if (!target.value || wasIntersected.value) return;

    const rect = target.value.getBoundingClientRect();
    const isAlreadyVisible = rect.top < window.innerHeight && rect.bottom > 0;

    if (isAlreadyVisible) {
      wasIntersected.value = true;
      callback?.();
      return;
    }

    observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[], observerInstance) => {
        entries.forEach((entry) => {
          if (
            entry.intersectionRatio >=
            (Array.isArray(defaultOptions.threshold)
              ? (defaultOptions.threshold[0] ?? 0)
              : (defaultOptions.threshold ?? 0))
          ) {
            wasIntersected.value = true;
            callback?.();
            observerInstance.disconnect();
          }
        });
      },
      defaultOptions,
    );

    observer.observe(target.value);
  };

  onMounted(() => {
    observe();
  });

  onUnmounted(() => {
    if (observer && target.value) {
      observer.unobserve(target.value);
      observer.disconnect();
    }
  });

  watch(target, (newVal) => {
    if (observer) {
      observer.disconnect();
    }
    wasIntersected.value = false;
    if (newVal) {
      observe();
    }
  });

  return {
    wasIntersected,
  };
}
